<ng-container *transloco="let t; read: 'fileManager'">
  <app-grid-page [title]="t('title')">
    <div class="flex h-full min-h-0 flex-col overflow-hidden rounded-card bg-card shadow-[0_0_0_1px_var(--border-strong)]">
      <!-- Card header: search, breadcrumb, view toggle, actions -->
      <div class="flex h-16 flex-none items-center gap-8 px-4 shadow-[inset_0_-1px_0_var(--border-strong)]">
        <div class="flex min-w-0 flex-1 items-center gap-6">
          <div class="w-[340px] flex-none">
            <app-text-input type="search" size="sm" [placeholder]="t('searchPlaceholder')" [(ngModel)]="query" name="q" />
          </div>

          <!-- Breadcrumb -->
          <nav class="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            @for (slot of breadcrumbSlots(); track $index; let last = $last) {
              @if (slot.kind === 'crumb') {
                @if (!last) {
                  <button
                    type="button"
                    (click)="navigateTo(slot.index)"
                    class="flex-none whitespace-nowrap text-body-1 font-semibold text-ink-2 hover:text-brand"
                  >
                    {{ slot.entry.name }}
                  </button>
                  <span class="flex-none text-ink-3">/</span>
                } @else {
                  <span class="min-w-0 flex-1 truncate text-body-1 font-semibold text-ink-1">
                    {{ slot.entry.name }}
                  </span>
                }
              } @else {
                <div class="relative flex-none">
                  <button
                    type="button"
                    (click)="toggleBreadcrumbMenu()"
                    [attr.aria-label]="t('collapsedCrumb')"
                    [attr.aria-expanded]="isBreadcrumbMenuOpen()"
                    class="whitespace-nowrap text-body-1 font-semibold text-ink-2 hover:text-brand"
                  >
                    &hellip;
                  </button>
                  @if (isBreadcrumbMenuOpen()) {
                    <div class="absolute left-0 top-full z-10 mt-1 min-w-[160px] rounded-control bg-card py-1 shadow-raised shadow-[0_0_0_1px_var(--border-strong)]">
                      @for (crumb of slot.entries; track crumb.index) {
                        <button
                          type="button"
                          (click)="navigateToCollapsed(crumb.index)"
                          class="block w-full truncate px-3 py-1.5 text-left text-body-1 text-ink-1 hover:bg-sunken"
                        >
                          {{ crumb.entry.name }}
                        </button>
                      }
                    </div>
                  }
                </div>
                <span class="flex-none text-ink-3">/</span>
              }
            }
          </nav>
        </div>

        <div class="flex flex-none items-center gap-4">
          <app-segmented-control
            [segments]="[
              { value: 'grid', label: t('view.grid') },
              { value: 'list', label: t('view.list') },
            ]"
            [ngModel]="viewMode()"
            (ngModelChange)="viewMode.set($event)"
          />
          <div class="h-8 w-px flex-none bg-[var(--border-strong)]"></div>
          <app-button variant="secondary" size="sm" iconLeft="icon-content-add24px" [label]="t('newFolder')" (activated)="openCreateFolder()" />
          <label class="inline-flex h-8 cursor-pointer items-center gap-2 rounded-control bg-action px-4 text-button font-semibold text-action-text shadow-raised hover:brightness-90">
            <app-icon name="icon-cloud" [size]="18" />
            {{ t('uploadFile') }}
            <input type="file" class="sr-only" (change)="onFileInputChange($event)" />
          </label>
        </div>
      </div>

      <!-- Content -->
      <div class="min-h-0 flex-1 overflow-y-auto" [attr.aria-busy]="isLoading() || null">
        @if (showSkeleton()) {
          <!-- Cold load: bones on the active view's real geometry, so nothing
               shifts when the folder contents land. -->
          @if (viewMode() === 'grid') {
            <div class="grid-skeleton grid grid-cols-4 gap-4 p-4" aria-hidden="true">
              @for (bone of skeletonItems; track $index) {
                <div class="flex h-[228.25px] flex-col overflow-hidden rounded-[8px] bg-surface-3 p-2 shadow-[inset_0_0_0_1px_var(--border-hairline)]">
                  <div class="flex-1 animate-pulse rounded bg-surface-2"></div>
                  <div class="flex min-h-[56px] flex-col justify-center gap-1.5 py-2.5">
                    <div class="h-3.5 w-3/4 animate-pulse rounded bg-surface-2"></div>
                    <div class="h-3.5 w-2/5 animate-pulse rounded bg-surface-2"></div>
                  </div>
                  <div class="min-h-[41px] pt-1 shadow-[inset_0_1px_0_var(--border-strong)]"></div>
                </div>
              }
            </div>
          } @else {
            <div class="grid-skeleton" aria-hidden="true">
              @for (bone of skeletonItems; track $index; let last = $last) {
                <div class="flex h-16 items-center gap-4 px-4" [class.shadow-[inset_0_-1px_0_var(--border-hairline)]]="!last">
                  <div class="h-10 w-14 flex-none animate-pulse rounded-[4px] bg-surface-2"></div>
                  <div class="min-w-0 flex-1"><div class="h-4 w-2/5 animate-pulse rounded bg-surface-2"></div></div>
                  <div class="w-[140px] flex-none"><div class="h-4 w-20 animate-pulse rounded bg-surface-2"></div></div>
                  <div class="flex w-[120px] flex-none justify-end"><div class="h-4 w-12 animate-pulse rounded bg-surface-2"></div></div>
                  <div class="flex w-[180px] flex-none justify-end"><div class="h-4 w-28 animate-pulse rounded bg-surface-2"></div></div>
                  <div class="w-[116px] flex-none"></div>
                </div>
              }
            </div>
          }
        } @else if (filteredItems().length === 0) {
          <div class="flex h-full min-h-40 flex-col items-center justify-center gap-4 px-6 text-center">
            <span class="font-display text-2xl font-bold tracking-[0.02em] text-ink-1">
              {{ query() ? t('emptyFolder.noResultsTitle') : t('emptyFolder.title') }}
            </span>
            <p class="max-w-[420px] text-body-1 text-ink-2">
              {{ query() ? t('emptyFolder.noResultsMessage') : t('emptyFolder.message') }}
            </p>
            @if (!query()) {
              <label class="inline-flex h-12 cursor-pointer items-center gap-2 rounded-control bg-action px-4 text-button font-semibold text-action-text shadow-raised hover:brightness-90">
                <app-icon name="icon-cloud" [size]="20" />
                {{ t('uploadFile') }}
                <input type="file" class="sr-only" (change)="onFileInputChange($event)" />
              </label>
            }
          </div>
        } @else if (viewMode() === 'grid') {
          <!-- Tile grid -->
          <div class="grid grid-cols-4 gap-4 p-4" [class.grid-refetching]="isRefetching()">
            @for (item of filteredItems(); track item.id) {
              <div
                class="flex h-[228.25px] flex-col overflow-hidden rounded-[8px] bg-surface-3 p-2 shadow-[inset_0_0_0_1px_var(--border-hairline)]"
                [class.cursor-pointer]="item.type === 'FOLDER'"
                [attr.role]="item.type === 'FOLDER' ? 'button' : null"
                [attr.tabindex]="item.type === 'FOLDER' ? 0 : null"
                (click)="onItemActivate(item)"
                (keydown.enter)="onItemActivate(item)"
              >
                <!-- Preview -->
                <div class="flex flex-1 items-center justify-center overflow-hidden bg-[var(--core-greyscale-700)] p-2">
                  @if (item.type === 'FOLDER') {
                    <app-icon name="icon-folder" [size]="56" class="text-white/75" />
                  } @else if (thumbnailPending(item)) {
                    <!-- Thumbnail blob still downloading — pulse instead of an
                         empty <img>, on the preview zone's dark background. -->
                    <div class="h-full w-full animate-pulse rounded bg-white/15"></div>
                  } @else if (isImage(item)) {
                    <img [src]="thumbnailUrl(item)" alt="" class="h-full w-full object-contain" />
                  } @else {
                    <app-icon name="icon-menu-content1" [size]="44" class="text-white/75" />
                  }
                </div>

                <!-- Title block -->
                <div class="flex min-h-[56px] flex-col justify-center py-2.5">
                  <div class="truncate text-body-1 font-semibold text-ink-1">{{ item.name }}</div>
                  <div class="truncate text-body-1 text-ink-2">
                    {{ item.type === 'FOLDER' ? t('kind.folder') : formatSize(item.size) }}
                  </div>
                </div>

                <!-- Action row -->
                <div class="flex min-h-[41px] items-center justify-end gap-1 pt-1 shadow-[inset_0_1px_0_var(--border-strong)]">
                  @if (item.type === 'FILE') {
                    <button
                      type="button"
                      [attr.aria-label]="t('actions.download')"
                      (click)="$event.stopPropagation(); downloadItem(item)"
                      class="flex h-8 w-8 items-center justify-center rounded-[8px] text-ink-1 hover:bg-[rgba(0,127,233,0.2)]"
                    >
                      <app-icon name="icon-import" [size]="24" />
                    </button>
                  }
                  <button
                    type="button"
                    [attr.aria-label]="t('actions.rename')"
                    (click)="$event.stopPropagation(); openRename(item)"
                    class="flex h-8 w-8 items-center justify-center rounded-[8px] text-ink-1 hover:bg-[rgba(0,127,233,0.2)]"
                  >
                    <app-icon name="icon-edit-pencil" [size]="24" />
                  </button>
                  <button
                    type="button"
                    [attr.aria-label]="t('actions.delete')"
                    (click)="$event.stopPropagation(); confirmDelete(item)"
                    class="flex h-8 w-8 items-center justify-center rounded-[8px] text-ink-1 hover:bg-[rgba(0,127,233,0.2)] hover:text-danger"
                  >
                    <app-icon name="icon-delete-trash" [size]="24" />
                  </button>
                </div>
              </div>
            }
          </div>
        } @else {
          <!-- List view -->
          <div class="flex h-12 flex-none items-center gap-4 px-4 shadow-[inset_0_-1px_0_var(--border-hairline)]">
            <span class="w-14 flex-none"></span>
            <span class="flex-1 text-caption font-semibold uppercase tracking-[0.04em] text-ink-3">{{ t('list.name') }}</span>
            <span class="w-[140px] flex-none text-caption font-semibold uppercase tracking-[0.04em] text-ink-3">{{ t('list.kind') }}</span>
            <span class="w-[120px] flex-none text-right text-caption font-semibold uppercase tracking-[0.04em] text-ink-3">{{ t('list.size') }}</span>
            <span class="w-[180px] flex-none text-right text-caption font-semibold uppercase tracking-[0.04em] text-ink-3">{{ t('list.modified') }}</span>
            <span class="w-[116px] flex-none"></span>
          </div>
          @for (item of filteredItems(); track item.id; let last = $last) {
            <div
              class="flex h-16 items-center gap-4 px-4 hover:bg-sunken"
              [class.cursor-pointer]="item.type === 'FOLDER'"
              [class.grid-refetching]="isRefetching()"
              [class.shadow-[inset_0_-1px_0_var(--border-hairline)]]="!last"
              [attr.role]="item.type === 'FOLDER' ? 'button' : null"
              [attr.tabindex]="item.type === 'FOLDER' ? 0 : null"
              (click)="onItemActivate(item)"
              (keydown.enter)="onItemActivate(item)"
            >
              <div class="flex h-10 w-14 flex-none items-center justify-center rounded-[4px] bg-[var(--core-greyscale-700)]">
                @if (item.type === 'FOLDER') {
                  <app-icon name="icon-folder" [size]="22" class="text-white/75" />
                } @else if (thumbnailPending(item)) {
                  <div class="h-10 w-14 animate-pulse rounded-[4px] bg-white/15"></div>
                } @else if (isImage(item)) {
                  <img [src]="thumbnailUrl(item)" alt="" class="h-10 w-14 rounded-[4px] object-contain" />
                } @else {
                  <app-icon name="icon-menu-content1" [size]="22" class="text-white/75" />
                }
              </div>
              <span class="min-w-0 flex-1 truncate text-body-1 font-semibold text-ink-1">{{ item.name }}</span>
              <span class="w-[140px] flex-none text-body-1 text-ink-2">
                {{ item.type === 'FOLDER' ? t('kind.folder') : (item.contentType ?? '—') }}
              </span>
              <span class="w-[120px] flex-none text-right text-body-1 text-ink-2">
                {{ item.type === 'FILE' ? formatSize(item.size) : '—' }}
              </span>
              <span class="w-[180px] flex-none text-right text-body-1 text-ink-2">
                {{ item.updatedAt | date: 'EEE MMM d · HH:mm' }}
              </span>
              <div class="flex w-[116px] flex-none items-center justify-end gap-1">
                @if (item.type === 'FILE') {
                  <button
                    type="button"
                    [attr.aria-label]="t('actions.download')"
                    (click)="$event.stopPropagation(); downloadItem(item)"
                    class="flex h-8 w-8 items-center justify-center rounded-[8px] text-ink-1 hover:bg-[rgba(0,127,233,0.2)]"
                  >
                    <app-icon name="icon-import" [size]="24" />
                  </button>
                }
                <button
                  type="button"
                  [attr.aria-label]="t('actions.rename')"
                  (click)="$event.stopPropagation(); openRename(item)"
                  class="flex h-8 w-8 items-center justify-center rounded-[8px] text-ink-1 hover:bg-[rgba(0,127,233,0.2)]"
                >
                  <app-icon name="icon-edit-pencil" [size]="24" />
                </button>
                <button
                  type="button"
                  [attr.aria-label]="t('actions.delete')"
                  (click)="$event.stopPropagation(); confirmDelete(item)"
                  class="flex h-8 w-8 items-center justify-center rounded-[8px] text-ink-1 hover:bg-[rgba(0,127,233,0.2)] hover:text-danger"
                >
                  <app-icon name="icon-delete-trash" [size]="24" />
                </button>
              </div>
            </div>
          }
        }
      </div>

      <!-- Footer: pagination -->
      <div class="flex h-16 flex-none items-center justify-between px-4 shadow-[inset_0_1px_0_var(--border-strong)]">
        <div class="flex items-center gap-1">
          <button
            type="button"
            (click)="prevPage()"
            [disabled]="page() === 0"
            [attr.aria-label]="t('pagination.previous')"
            class="flex h-8 w-8 items-center justify-center rounded-[8px] text-ink-1 hover:bg-sunken disabled:opacity-40"
          >
            <app-icon name="icon-arrow-left" [size]="20" />
          </button>
          <button
            type="button"
            (click)="nextPage()"
            [disabled]="page() >= totalPages() - 1"
            [attr.aria-label]="t('pagination.next')"
            class="flex h-8 w-8 items-center justify-center rounded-[8px] text-ink-1 hover:bg-sunken disabled:opacity-40"
          >
            <app-icon name="icon-arrow-right" [size]="20" />
          </button>
        </div>
        <span class="text-body-1 text-ink-2">{{ rangeLabel() }}</span>
        <select
          [ngModel]="pageSize()"
          (ngModelChange)="setPageSize(+$event)"
          class="h-8 rounded-control bg-card px-2 text-body-1 text-ink-1 shadow-[0_0_0_1px_var(--border-strong)]"
        >
          @for (size of pageSizeOptions; track size) {
            <option [value]="size">{{ t('pagination.perPage', { count: size }) }}</option>
          }
        </select>
      </div>
    </div>
  </app-grid-page>
</ng-container>

<!-- Upload dialog -->
@if (isUploadOpen()) {
  <ng-container *transloco="let t; read: 'fileManager'">
    <app-modal [(open)]="isUploadOpen" [title]="t('upload.title')" [subtitle]="t('upload.subtitle')" size="small" (closed)="closeUpload()">
      @if (pendingUploadFile(); as file) {
        <div class="mb-4 flex items-center gap-2 rounded-control bg-sunken px-3 py-2.5 text-body-1 text-ink-2">
          <app-icon name="icon-menu-content1" [size]="18" class="flex-none text-ink-3" />
          <span class="min-w-0 flex-1 truncate">{{ file.name }}</span>
          <span class="flex-none text-ink-3">{{ formatSize(file.size) }}</span>
        </div>
      }
      <div class="space-y-4">
        <app-text-input [label]="t('upload.name')" [placeholder]="t('upload.namePlaceholder')" [(ngModel)]="uploadName" name="uploadName" />
        <app-form-field [help]="t('upload.descriptionHelp')">
          <app-text-input
            [label]="t('upload.description')"
            [placeholder]="t('upload.descriptionPlaceholder')"
            [(ngModel)]="uploadDescription"
            name="uploadDescription"
          />
        </app-form-field>
      </div>

      <ng-template modalFooter>
        <div class="flex flex-1 items-center justify-end gap-2">
          <app-button variant="secondary" size="sm" [label]="t('upload.cancel')" (activated)="closeUpload()" />
          <app-button variant="primary" size="sm" [label]="t('upload.submit')" [disabled]="!uploadName.trim()" (activated)="submitUpload()" />
        </div>
      </ng-template>
    </app-modal>
  </ng-container>
}

<!-- Rename dialog -->
@if (isRenaming()) {
  <ng-container *transloco="let t; read: 'fileManager'">
    <app-modal [(open)]="isRenaming" [title]="t('rename.title')" size="small" (closed)="closeRename()">
      <app-text-input
        [label]="t('rename.name')"
        [placeholder]="t('rename.namePlaceholder')"
        [(ngModel)]="renameValue"
        name="renameValue"
        (keyup.enter)="submitRename()"
      />
      <ng-template modalFooter>
        <div class="flex flex-1 items-center justify-end gap-2">
          <app-button variant="secondary" size="sm" [label]="t('rename.cancel')" (activated)="closeRename()" />
          <app-button variant="primary" size="sm" [label]="t('rename.submit')" [disabled]="!renameValue.trim()" (activated)="submitRename()" />
        </div>
      </ng-template>
    </app-modal>
  </ng-container>
}

<!-- New folder dialog -->
@if (isCreatingFolder()) {
  <ng-container *transloco="let t; read: 'fileManager'">
    <app-modal [(open)]="isCreatingFolder" [title]="t('createFolder.title')" size="small" (closed)="closeCreateFolder()">
      <app-text-input
        [label]="t('createFolder.name')"
        [placeholder]="t('createFolder.namePlaceholder')"
        [(ngModel)]="newFolderName"
        name="newFolderName"
        (keyup.enter)="submitCreateFolder()"
      />
      <ng-template modalFooter>
        <div class="flex flex-1 items-center justify-end gap-2">
          <app-button variant="secondary" size="sm" [label]="t('createFolder.cancel')" (activated)="closeCreateFolder()" />
          <app-button variant="primary" size="sm" [label]="t('createFolder.submit')" [disabled]="!newFolderName.trim()" (activated)="submitCreateFolder()" />
        </div>
      </ng-template>
    </app-modal>
  </ng-container>
}
